import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading2, Heading3, AlignLeft, AlignCenter, AlignRight, Palette, Eraser } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

const COLORS = ["#0f172a", "#E05638", "#059669", "#2563eb", "#9333ea", "#dc2626", "#64748b"];

// Éditeur de texte riche léger basé sur contentEditable (aucune dépendance).
export function RichTextEditor({ value = "", onChange }) {
  const ref = useRef(null);

  useEffect(() => {
    const clean = sanitizeHtml(value);
    if (ref.current && ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    ref.current?.focus();
    onChange?.(ref.current?.innerHTML || "");
  };

  const makeLink = () => {
    const url = window.prompt("Adresse du lien (https://…)");
    if (url) exec("createLink", url);
  };

  const Btn = ({ onClick, title, children, testId }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      data-testid={testId}
      className="h-8 w-8 grid place-items-center rounded-md text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors">
      {children}
    </button>
  );

  return (
    <div className="border border-slate-300 rounded-xl overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1">
        <Btn onClick={() => exec("bold")} title="Gras" testId="rte-bold"><Bold size={15} /></Btn>
        <Btn onClick={() => exec("italic")} title="Italique" testId="rte-italic"><Italic size={15} /></Btn>
        <Btn onClick={() => exec("underline")} title="Souligné" testId="rte-underline"><Underline size={15} /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => exec("formatBlock", "<h2>")} title="Titre" testId="rte-h2"><Heading2 size={15} /></Btn>
        <Btn onClick={() => exec("formatBlock", "<h3>")} title="Sous-titre" testId="rte-h3"><Heading3 size={15} /></Btn>
        <Btn onClick={() => exec("insertUnorderedList")} title="Liste à puces" testId="rte-ul"><List size={15} /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Liste numérotée" testId="rte-ol"><ListOrdered size={15} /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => exec("justifyLeft")} title="Aligner à gauche" testId="rte-left"><AlignLeft size={15} /></Btn>
        <Btn onClick={() => exec("justifyCenter")} title="Centrer" testId="rte-center"><AlignCenter size={15} /></Btn>
        <Btn onClick={() => exec("justifyRight")} title="Aligner à droite" testId="rte-right"><AlignRight size={15} /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={makeLink} title="Insérer un lien" testId="rte-link"><Link2 size={15} /></Btn>
        <span className="inline-flex items-center gap-0.5 px-1">
          <Palette size={14} className="text-slate-500" />
          {COLORS.map((c) => (
            <button key={c} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("foreColor", c)}
              title={`Couleur ${c}`} className="h-4 w-4 rounded-full border border-white shadow" style={{ background: c }} />
          ))}
        </span>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => exec("removeFormat")} title="Effacer la mise en forme" testId="rte-clear"><Eraser size={15} /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange?.(ref.current?.innerHTML || "")}
        data-testid="rte-area"
        className="cms-rich min-h-[120px] px-3 py-2 text-slate-800 outline-none prose-sm max-w-none"
      />
    </div>
  );
}

export default RichTextEditor;
